/**
 * Utilitaires centralisés pour le formatage des dates.
 * Toute l'application doit utiliser ces fonctions.
 */

/**
 * Formate une date en "dd/MM/yyyy".
 * Retourne "—" si la date est null, undefined ou invalide (jamais "Invalid Date").
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '—';
  }
}

/**
 * Formate une date en "dd/MM/yyyy HH:mm".
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
}

/**
 * Retourne une durée relative lisible :
 * - passé : "Il y a 3 jours", "Il y a 2 mois"
 * - futur : "Dans 5 mois", "Dans 12 jours"
 */
export function formatRelativeDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '—';

    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const absDiffMs = Math.abs(diffMs);
    const isPast = diffMs < 0;

    const minutes = Math.floor(absDiffMs / (1000 * 60));
    const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
    const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
    const months = Math.floor(days / 30.44);
    const years = Math.floor(days / 365.25);

    let label: string;
    if (minutes < 1) label = "à l'instant";
    else if (minutes < 60) label = `${minutes} min`;
    else if (hours < 24) label = `${hours}h`;
    else if (days < 30) label = `${days} jour${days > 1 ? 's' : ''}`;
    else if (months < 12) label = `${months} mois`;
    else label = `${years} an${years > 1 ? 's' : ''}`;

    if (label === "à l'instant") return label;
    return isPast ? `Il y a ${label}` : `Dans ${label}`;
  } catch {
    return '—';
  }
}

/**
 * Calcule la durée restante ou dépassée entre maintenant et une date de fin.
 * Retourne { label: string, percent: number, isExpired: boolean }
 */
export function getTimeProgress(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
): { label: string; percent: number; isExpired: boolean } {
  if (!startDate || !endDate) return { label: '—', percent: 0, isExpired: false };
  try {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { label: '—', percent: 0, isExpired: false };
    }

    const now = new Date();
    const totalMs = end.getTime() - start.getTime();
    const elapsedMs = now.getTime() - start.getTime();
    const percent = totalMs > 0 ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100)) : 0;
    const isExpired = now > end;

    const diffMs = Math.abs(end.getTime() - now.getTime());
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const months = Math.floor(days / 30.44);

    const remainingDays = days - Math.floor(months * 30.44);

    let label: string;
    if (isExpired) {
      if (days < 30) {
        label = `Expiré depuis ${days} jour${days > 1 ? 's' : ''}`;
      } else if (remainingDays > 0) {
        label = `Expiré depuis ${months} mois et ${remainingDays} jour${remainingDays > 1 ? 's' : ''}`;
      } else {
        label = `Expiré depuis ${months} mois`;
      }
    } else {
      if (days < 30) {
        label = `${days} jour${days > 1 ? 's' : ''} restant${days > 1 ? 's' : ''}`;
      } else if (remainingDays > 0) {
        label = `${months} mois et ${remainingDays} jour${remainingDays > 1 ? 's' : ''} restants`;
      } else {
        label = `${months} mois restant${months > 1 ? 's' : ''}`;
      }
    }

    return { label, percent, isExpired };
  } catch {
    return { label: '—', percent: 0, isExpired: false };
  }
}

/**
 * Formate une durée écoulée lisible : "4 mois et 19 jours"
 */
export function formatElapsedDuration(
  startDate: string | Date | null | undefined,
  endDate?: string | Date | null | undefined,
): string {
  if (!startDate) return '—';
  try {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    if (isNaN(start.getTime())) return '—';

    const end = endDate ? (endDate instanceof Date ? endDate : new Date(endDate)) : new Date();
    if (isNaN(end.getTime())) return '—';

    const diffMs = Math.abs(end.getTime() - start.getTime());
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const months = Math.floor(totalDays / 30.44);
    const days = Math.round(totalDays - months * 30.44);

    if (months === 0) return `${days} jour${days > 1 ? 's' : ''}`;
    if (days === 0) return `${months} mois`;
    return `${months} mois et ${days} jour${days > 1 ? 's' : ''}`;
  } catch {
    return '—';
  }
}
